import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, Maximize, Film } from 'lucide-react';
import { fmtTime } from './EditorTimelineRuler';
import { EditorCompositionPreview } from './EditorCompositionPreview';

/**
 * Area 2 — Preview Player. Stage 3B replaces the single-layer "topmost
 * track wins" placeholder with real composition-aware rendering handled
 * by EditorCompositionPreview (multi-layer video, PiP, crop, transform,
 * keyframes, fades, mute/hidden rules). This file keeps owning transport:
 * the real-time playhead clock, scrub bar, fullscreen, and the fit/fill
 * toggle. Reuses the EXISTING B2 presigned stream_url per source — never
 * redesigns or reconnects B2.
 */
export function EditorPreviewPlayer({ composition, siblingSources, playhead, setPlayhead, isPlaying, setIsPlaying, onDurationKnown }) {
  const containerRef = useRef(null);
  const [fit, setFit] = useState('contain');

  const totalDuration = composition?.duration_seconds || 0;
  const aspectRatio = useMemo(() => `${composition?.width || 16} / ${composition?.height || 9}`, [composition?.width, composition?.height]);

  const hasActiveContent = useMemo(() => {
    if (!composition) return false;
    return composition.timeline_items.some((i) => {
      if (i.item_type === 'text') return false;
      const track = composition.tracks.find((t) => t.id === i.track_id);
      if (!track || track.hidden) return false;
      return playhead >= i.timeline_start_seconds && playhead < i.timeline_start_seconds + i.duration_seconds;
    });
  }, [composition, playhead]);

  // Master playhead clock — advances in real time while playing, driving
  // both the preview and (via shared state) the timeline's playhead.
  useEffect(() => {
    if (!isPlaying) return undefined;
    let raf;
    let last = performance.now();
    const tick = (ts) => {
      const dt = (ts - last) / 1000;
      last = ts;
      setPlayhead((t) => {
        const next = t + dt;
        if (next >= totalDuration) {
          setIsPlaying(false);
          return totalDuration;
        }
        return next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying, totalDuration, setPlayhead, setIsPlaying]);

  const handleScrub = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setPlayhead(pct * (totalDuration || 1));
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else containerRef.current.requestFullscreen?.();
  };

  return (
    <div className="space-y-2" data-testid="editor-master-preview-player">
      <div
        ref={containerRef}
        className="relative bg-black rounded-xl overflow-hidden border border-white/10 mx-auto"
        style={{ aspectRatio, maxHeight: '60vh', width: '100%' }}
      >
        {hasActiveContent ? (
          <EditorCompositionPreview
            composition={composition} siblingSources={siblingSources} playhead={playhead}
            isPlaying={isPlaying} fit={fit} onDurationKnown={onDurationKnown}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-center p-6" data-testid="master-preview-unavailable">
            <div>
              <Film className="h-10 w-10 text-[#555E6E] mx-auto mb-2" />
              <p className="text-sm text-[#8A93A6]">
                {composition?.timeline_items?.length ? 'Nothing on the timeline at this position' : 'Add a source from the Media Panel to begin'}
              </p>
            </div>
          </div>
        )}

        <button
          onClick={() => setIsPlaying((p) => !p)}
          className="absolute inset-0 flex items-center justify-center group"
          data-testid="master-play-pause-button"
        >
          <span className="h-14 w-14 rounded-full bg-black/40 group-hover:bg-black/60 flex items-center justify-center transition-colors">
            {isPlaying ? <Pause className="h-6 w-6 text-white" /> : <Play className="h-6 w-6 text-white ml-0.5" />}
          </span>
        </button>

        <button
          onClick={() => setFit((f) => (f === 'contain' ? 'cover' : 'contain'))}
          className="absolute top-3 right-12 bg-black/60 text-white text-[10px] px-2 py-1 rounded-md hover:bg-black/80"
          data-testid="master-preview-fit-toggle"
        >
          {fit === 'contain' ? 'Fit' : 'Fill'}
        </button>
        <button
          onClick={toggleFullscreen}
          className="absolute top-3 right-3 h-6 w-6 rounded-md bg-black/60 text-white hover:bg-black/80 flex items-center justify-center"
          data-testid="master-preview-fullscreen-button"
        >
          <Maximize className="h-3.5 w-3.5" />
        </button>
      </div>

      <div
        onClick={handleScrub}
        className="h-2 rounded-full bg-white/10 cursor-pointer relative"
        data-testid="master-preview-scrub-bar"
      >
        <div
          className="h-full bg-[#3A7BFF] rounded-full"
          style={{ width: `${totalDuration ? Math.min(100, (playhead / totalDuration) * 100) : 0}%` }}
        />
      </div>
      <div className="flex justify-between text-[11px] text-[#8A93A6]" data-testid="master-preview-time">
        <span>{fmtTime(playhead)}</span>
        <span>{fmtTime(totalDuration)}</span>
      </div>
    </div>
  );
}
